import React from 'react';
import {
  Box,
  VStack,
  Text,
  Icon,
  HStack,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import {
  Home,
  Brain,
  Database,
  GitBranch,
  Settings,
  Users,
  Shield,
  FileText,
  BarChart2,
  Network,
  TrendingUp,
  HelpCircle,
} from 'lucide-react';

interface NavItemProps {
  icon: any;
  label: string;
  isActive?: boolean;
  count?: number;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, isActive, count }) => {
  const activeBg = useColorModeValue('methodist.blue.50', 'methodist.blue.900');
  const activeColor = useColorModeValue('methodist.blue', 'white');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const countBg = useColorModeValue('methodist.blue.100', 'methodist.blue.800');

  return (
    <HStack
      py={2}
      px={4}
      cursor="pointer"
      borderRadius="md"
      bg={isActive ? activeBg : 'transparent'}
      color={isActive ? activeColor : 'inherit'}
      _hover={{
        bg: isActive ? activeBg : hoverBg,
      }}
      transition="all 0.2s"
    >
      <Icon as={icon} boxSize={5} />
      <Text flex={1}>{label}</Text>
      {count !== undefined && (
        <Box
          px={2}
          py={0.5}
          borderRadius="full"
          fontSize="xs"
          fontWeight="medium"
          bg={countBg}
          color={activeColor}
        >
          {count}
        </Box>
      )}
    </HStack>
  );
};

interface NexusAdminSidebarProps {
  activeSection: string;
}

export const NexusAdminSidebar: React.FC<NexusAdminSidebarProps> = ({
  activeSection
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const sections = {
    overview: [
      { icon: Home, label: 'Dashboard' },
      { icon: Brain, label: 'AI Models', count: 12 },
      { icon: Database, label: 'Data Pipelines', count: 8 },
      { icon: GitBranch, label: 'Model Versions' },
    ],
    management: [
      { icon: Shield, label: 'Access Control' },
      { icon: Users, label: 'User Management' },
      { icon: FileText, label: 'Policy Management' },
      { icon: Database, label: 'Data Governance' },
    ],
    analytics: [
      { icon: BarChart2, label: 'Performance' },
      { icon: Network, label: 'Infrastructure' },
      { icon: TrendingUp, label: 'Predictions' },
    ],
  };

  return (
    <Box
      w="250px"
      h="100vh"
      borderRight="1px"
      borderColor={borderColor}
      py={4}
      position="sticky"
      top={0}
    >
      {/* Logo */}
      <Box px={6} mb={8}>
        <Text fontSize="lg" fontWeight="bold" color="methodist.blue">
          JIMI Nexus
        </Text>
        <Text fontSize="xs" color="gray.500">
          Admin Portal
        </Text>
      </Box>

      <VStack spacing={6} align="stretch">
        {/* Overview Section */}
        <Box>
          <Text fontSize="xs" fontWeight="medium" color="gray.500" px={6} mb={2}>
            OVERVIEW
          </Text>
          <VStack spacing={1} align="stretch">
            {sections.overview.map((item) => (
              <NavItem
                key={item.label}
                {...item}
                isActive={activeSection === item.label.toLowerCase()}
              />
            ))}
          </VStack>
        </Box>

        <Divider />

        {/* Management Section */}
        <Box>
          <Text fontSize="xs" fontWeight="medium" color="gray.500" px={6} mb={2}>
            MANAGEMENT
          </Text>
          <VStack spacing={1} align="stretch">
            {sections.management.map((item) => (
              <NavItem
                key={item.label}
                {...item}
                isActive={activeSection === item.label.toLowerCase()}
              />
            ))}
          </VStack>
        </Box>

        <Divider />

        {/* Analytics Section */}
        <Box>
          <Text fontSize="xs" fontWeight="medium" color="gray.500" px={6} mb={2}>
            ANALYTICS
          </Text>
          <VStack spacing={1} align="stretch">
            {sections.analytics.map((item) => (
              <NavItem
                key={item.label}
                {...item}
                isActive={activeSection === item.label.toLowerCase()}
              />
            ))}
          </VStack>
        </Box>

        <Box mt="auto">
          <NavItem icon={Settings} label="Settings" />
          <NavItem icon={HelpCircle} label="Help & Support" />
        </Box>
      </VStack>
    </Box>
  );
};
